import {loadStripe} from '@stripe/stripe-js';
import {useCallback, useEffect, useRef, useState} from 'react';
import Sentry from 'sentry';
import {t} from 'ttag';

import {MoneyRequest} from '../../ClientInboxItem/types';
import {
    useGetMoneyRequestIsPaidView,
    usePostCreateCheckoutSessionView,
} from './services';

interface UseRedirectWorkflowProps {
    moneyRequest: MoneyRequest;
}

const useRedirectWorkflow = ({moneyRequest}: UseRedirectWorkflowProps) => {
    const genericErrorMessage = t`There seems to be something wrong with our payment system at the moment, please try again in a couple of minutes. If this problem persists, please contact us at support@makeleaps.com`;

    const [errorMessage, setErrorMessage] = useState<string>();
    const [isPaid, setIsPaid] = useState<boolean>(moneyRequest.paid);
    const [isLoading, setIsLoading] = useState<boolean>(
        moneyRequest.isPendingPayment,
    );

    const pollCount = useRef<number>(0);
    const getMoneyRequestIsPaidView = useGetMoneyRequestIsPaidView();
    const postCreateCheckoutSessionView = usePostCreateCheckoutSessionView();

    const onClick = useCallback(async () => {
        setIsLoading(true);
        setErrorMessage(undefined);

        // Step 1: Call your backend to create the Checkout Session
        const response = await postCreateCheckoutSessionView
            .mutateAsync({
                mrToken: moneyRequest.mrToken,
                palLink: window.location.href.split('?')[0],
            })
            .catch((error) => {
                if (error.message === 'already_paid') {
                    setIsPaid(true);
                } else {
                    setErrorMessage(genericErrorMessage);
                    setIsLoading(false);
                }
            });

        if (!response) return;

        // Step 2: Load Stripe
        const stripe = await loadStripe(moneyRequest.stripeKey, {
            apiVersion: '2020-08-27',
        });

        if (!stripe) {
            Sentry.captureMessage('Failed to load Stripe library', {
                extra: {
                    charge_mid: undefined,
                    mr_token: moneyRequest.mrToken,
                },
            });

            setErrorMessage(genericErrorMessage);
            setIsLoading(false);

            return;
        }

        // Step 3: Redirect to Stripe Checkout
        return stripe
            .redirectToCheckout({sessionId: response.data.sessionId})
            .catch((error) => {
                // If `redirectToCheckout` fails due to a browser or network
                // error, you should display the localized error message to your
                // customer using `error.message`.
                Sentry.captureException(error, {
                    extra: {
                        charge_mid: undefined,
                        mr_token: moneyRequest.mrToken,
                        sessionId: response.data.sessionId,
                    },
                });

                setErrorMessage(genericErrorMessage);
                setIsLoading(false);
            });
    }, [
        genericErrorMessage,
        moneyRequest.mrToken,
        moneyRequest.stripeKey,
        postCreateCheckoutSessionView,
    ]);

    useEffect(() => {
        if (moneyRequest.isPendingPayment && pollCount.current === 0) {
            const pollFunction = async () => {
                const response = await getMoneyRequestIsPaidView.mutateAsync({
                    mrMid: moneyRequest.mrMid,
                });

                if (response?.data.paid) {
                    setIsPaid(true);
                } else if (pollCount.current > 30) {
                    setErrorMessage(genericErrorMessage);
                } else {
                    pollCount.current += 1;
                    setTimeout(pollFunction, 500);
                }
            };
            pollCount.current += 1;
            setTimeout(pollFunction, 500);
        }
    }, [
        genericErrorMessage,
        getMoneyRequestIsPaidView,
        moneyRequest.isPendingPayment,
        moneyRequest.mrMid,
    ]);

    return {
        errorMessage,
        isLoading,
        isPaid,
        onClick,
    };
};

export default useRedirectWorkflow;
